import {
  PCM_SAMPLE_RATE,
  isPermanentProxyError,
  longformAudioProxyProtocol,
  type AudioProxyProtocol,
  type LongformProxyErrorCode,
  type ProxySessionStartedMessage,
} from "./audio-proxy-protocol";
import type { RawChunk } from "./audio-chunker";
import { float32ToBase64 } from "./audio-encoding";
import {
  MAX_BUFFERED_AUDIO_MS,
  MAX_BUFFERED_CHUNKS,
  MAX_SOCKET_BUFFERED_BYTES,
  RECONNECT_DELAYS_MS,
  SOCKET_BACKPRESSURE_POLL_MS,
  SOCKET_CONNECT_TIMEOUT_MS,
  SOCKET_HANDSHAKE_TIMEOUT_MS,
  SOCKET_STOP_DRAIN_TIMEOUT_MS,
} from "./audio-config";
import {
  MockAudioProxyTransport,
  WebSocketAudioProxyTransport,
  type AudioProxyCloseEvent,
  type AudioProxyConnection,
  type AudioProxyConnectionHandlers,
  type AudioProxyTransport,
} from "./audio-proxy-transport";

export const AUDIO_STREAMING_DEFAULTS = {
  maxBufferedChunks: MAX_BUFFERED_CHUNKS,
  maxBufferedDurationMs: MAX_BUFFERED_AUDIO_MS,
  maxSocketBufferedAmount: MAX_SOCKET_BUFFERED_BYTES,
  connectTimeoutMs: SOCKET_CONNECT_TIMEOUT_MS,
  handshakeTimeoutMs: SOCKET_HANDSHAKE_TIMEOUT_MS,
  backpressurePollMs: SOCKET_BACKPRESSURE_POLL_MS,
  stopDrainTimeoutMs: SOCKET_STOP_DRAIN_TIMEOUT_MS,
  endTimeoutMs: 1_000,
  reconnectDelaysMs: RECONNECT_DELAYS_MS,
} as const;

export const AUDIO_PROXY_SESSION_STORAGE_PREFIX = "longform-audio-proxy:";

export type AudioProxyMode = "disabled" | "mock" | "websocket";

export interface AudioStreamingRuntimeConfig {
  mode: AudioProxyMode;
  url?: string;
  apiKey?: string;
  configurationError?: string;
}

/** Converts Vite's string environment into safe runtime options. */
export function readAudioStreamingConfig(
  environment: Record<string, string | boolean | undefined> =
    import.meta.env as unknown as Record<string, string | boolean | undefined>,
): AudioStreamingRuntimeConfig {
  const requestedMode = String(
    environment.VITE_AUDIO_PROXY_MODE ?? "disabled",
  )
    .trim()
    .toLowerCase();
  const url = stringEnvironmentValue(environment.VITE_AUDIO_PROXY_URL);
  const apiKey = stringEnvironmentValue(environment.VITE_AUDIO_PROXY_API_KEY);

  if (
    requestedMode !== "disabled" &&
    requestedMode !== "mock" &&
    requestedMode !== "websocket"
  ) {
    return {
      mode: "disabled",
      configurationError: `Audio streaming mode "${requestedMode}" is not supported.`,
    };
  }

  return { mode: requestedMode, url, apiKey };
}

export type AudioStreamingState =
  | "idle"
  | "unavailable"
  | "connecting"
  | "awaiting_ack"
  | "streaming"
  | "reconnecting"
  | "ending"
  | "degraded"
  | "stopped";

export interface AudioStreamingSnapshot {
  state: AudioStreamingState;
  /** Local recorder/R2/D1 ID, never sent as a new proxy session ID. */
  sessionId?: string;
  /** Server-issued LongformAudioProxy session ID. */
  proxySessionId?: string;
  proxyResumed?: boolean;
  proxyTotalBytes?: number;
  lastAcknowledgedSequenceNumber?: number;
  paused: boolean;
  queuedChunks: number;
  queuedDurationMs: number;
  nextSequenceNumber: number;
  reconnectAttempt: number;
  bufferedAmount: number;
  actualSampleRate?: number;
  actualChannelCount?: number;
  handshakeLatencyMs?: number;
  error?: string;
}

/**
 * The durable recorder and the proxy intentionally have independent IDs.
 * `localRecordingId` is never used as a new LongformAudioProxy session ID.
 */
export interface RecordingSessionIds {
  localRecordingId: string;
  proxySessionId?: string;
}

export interface AudioStreamingStartOptions {
  /**
   * Local capture ID used only for logging and proxy-ID sessionStorage mapping.
   * Keeping it distinct avoids an invalid resume request on first proxy connect.
   */
  sessionId?: RecordingSessionIds["localRecordingId"];
  /** Explicit server-issued proxy ID, useful when restoring a prior browser session. */
  proxySessionId?: RecordingSessionIds["proxySessionId"];
  /**
   * Runtime-only proxy credential for this streaming session. It overrides the
   * Vite-configured fallback and is retained in memory only for handshakes.
   */
  sessionKey?: string;
  /** Alias for sessionKey for callers that use the protocol's `key` terminology. */
  apiKey?: string;
  actualSampleRate?: number;
  actualChannelCount?: number;
}

export interface ProxySessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AudioStreamingLoggerFields {
  sessionId?: string;
  proxySessionId?: string;
  state: AudioStreamingState;
  sequenceNumber?: number;
  queueDepth?: number;
  queueDurationMs?: number;
  bufferedAmount?: number;
  reconnectAttempt?: number;
  handshakeLatencyMs?: number;
  errorCode?: string;
  errorMessage?: string;
  actualSampleRate?: number;
  actualChannelCount?: number;
}

export type AudioStreamingLogger = (
  event: string,
  fields: AudioStreamingLoggerFields,
) => void;

export interface AudioStreamingSessionOptions {
  mode?: AudioProxyMode;
  url?: string;
  apiKey?: string;
  configurationError?: string;
  protocol?: AudioProxyProtocol;
  transport?: AudioProxyTransport;
  proxySessionStorage?: ProxySessionStorage;
  maxBufferedChunks?: number;
  maxBufferedDurationMs?: number;
  maxSocketBufferedAmount?: number;
  connectTimeoutMs?: number;
  handshakeTimeoutMs?: number;
  backpressurePollMs?: number;
  stopDrainTimeoutMs?: number;
  endTimeoutMs?: number;
  reconnectDelaysMs?: readonly number[];
  jitter?: (delayMs: number, attempt: number) => number;
  now?: () => number;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  cancel?: (handle: unknown) => void;
  logger?: AudioStreamingLogger;
}

interface PendingChunk {
  sequenceNumber: number;
  message: string;
  durationMs: number;
}

interface ResolvedOptions {
  mode: AudioProxyMode;
  url?: string;
  apiKey: string;
  configurationError?: string;
  protocol: AudioProxyProtocol;
  transport?: AudioProxyTransport;
  proxySessionStorage?: ProxySessionStorage;
  maxBufferedChunks: number;
  maxBufferedDurationMs: number;
  maxSocketBufferedAmount: number;
  connectTimeoutMs: number;
  handshakeTimeoutMs: number;
  backpressurePollMs: number;
  stopDrainTimeoutMs: number;
  endTimeoutMs: number;
  reconnectDelaysMs: readonly number[];
  jitter: (delayMs: number, attempt: number) => number;
  now: () => number;
  schedule: (callback: () => void, delayMs: number) => unknown;
  cancel: (handle: unknown) => void;
  logger?: AudioStreamingLogger;
}

/**
 * Ordered, bounded sidecar for LongformAudioProxy. It is intentionally
 * independent from MediaRecorder and the durable Cloudflare upload queue.
 */
export class AudioStreamingSession {
  private readonly options: ResolvedOptions;
  private snapshot: AudioStreamingSnapshot = {
    state: "idle",
    paused: false,
    queuedChunks: 0,
    queuedDurationMs: 0,
    nextSequenceNumber: 0,
    reconnectAttempt: 0,
    bufferedAmount: 0,
  };
  private localSessionId?: string;
  private proxySessionId?: string;
  /** Never copied to snapshots, sessionStorage, mock metadata, or logs. */
  private handshakeApiKey = "";
  private connection?: AudioProxyConnection;
  private connectionGeneration = 0;
  private connectionAbort?: AbortController;
  private handledGenerations = new Set<number>();
  private acknowledged = false;
  /** True after the proxy has issued a server session ID at least once. */
  private established = false;
  private stopping = false;
  private disposed = false;
  private sessionEnded = false;
  private queue: PendingChunk[] = [];
  private queueDurationMs = 0;
  private listeners = new Set<(snapshot: AudioStreamingSnapshot) => void>();
  private connectTimer?: unknown;
  private handshakeTimer?: unknown;
  private reconnectTimer?: unknown;
  private backpressureTimer?: unknown;
  private handshakeStartedAt?: number;
  private drainWaiters = new Set<() => void>();
  private endWaiters = new Set<(ended: boolean) => void>();
  private stopPromise?: Promise<void>;

  constructor(options: AudioStreamingSessionOptions = {}) {
    const mode = options.mode ?? "disabled";
    this.options = {
      mode,
      url: options.url,
      // Mock mode is usable without exposing a real browser credential.
      apiKey: options.apiKey ?? (mode === "mock" ? "mock-key" : ""),
      configurationError: options.configurationError,
      protocol: options.protocol ?? longformAudioProxyProtocol,
      transport:
        options.transport ??
        (mode === "mock"
          ? new MockAudioProxyTransport()
          : mode === "websocket"
            ? new WebSocketAudioProxyTransport()
            : undefined),
      proxySessionStorage:
        options.proxySessionStorage ?? defaultProxySessionStorage(),
      maxBufferedChunks:
        options.maxBufferedChunks ?? AUDIO_STREAMING_DEFAULTS.maxBufferedChunks,
      maxBufferedDurationMs:
        options.maxBufferedDurationMs ??
        AUDIO_STREAMING_DEFAULTS.maxBufferedDurationMs,
      maxSocketBufferedAmount:
        options.maxSocketBufferedAmount ??
        AUDIO_STREAMING_DEFAULTS.maxSocketBufferedAmount,
      connectTimeoutMs:
        options.connectTimeoutMs ?? AUDIO_STREAMING_DEFAULTS.connectTimeoutMs,
      handshakeTimeoutMs:
        options.handshakeTimeoutMs ?? AUDIO_STREAMING_DEFAULTS.handshakeTimeoutMs,
      backpressurePollMs:
        options.backpressurePollMs ?? AUDIO_STREAMING_DEFAULTS.backpressurePollMs,
      stopDrainTimeoutMs:
        options.stopDrainTimeoutMs ??
        AUDIO_STREAMING_DEFAULTS.stopDrainTimeoutMs,
      endTimeoutMs:
        options.endTimeoutMs ?? AUDIO_STREAMING_DEFAULTS.endTimeoutMs,
      reconnectDelaysMs:
        options.reconnectDelaysMs ?? AUDIO_STREAMING_DEFAULTS.reconnectDelaysMs,
      jitter: options.jitter ?? jitteredDelay,
      now: options.now ?? Date.now,
      schedule:
        options.schedule ??
        ((callback, delayMs) => globalThis.setTimeout(callback, delayMs)),
      cancel:
        options.cancel ??
        ((handle) =>
          globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>)),
      logger: options.logger,
    };
  }

  getSnapshot(): AudioStreamingSnapshot {
    return this.snapshot;
  }

  getProxySessionId(): string | undefined {
    return this.proxySessionId;
  }

  subscribe(listener: (snapshot: AudioStreamingSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot);
    return () => this.listeners.delete(listener);
  }

  start(options: AudioStreamingStartOptions = {}): void {
    if (this.disposed || this.snapshot.state !== "idle") return;

    this.localSessionId = options.sessionId;
    this.proxySessionId =
      options.proxySessionId ?? this.readPersistedProxySessionId();
    this.handshakeApiKey = resolveSessionApiKey(
      options.sessionKey,
      options.apiKey,
      this.options.apiKey,
    );
    this.update({
      ...this.snapshot,
      sessionId: this.localSessionId,
      proxySessionId: this.proxySessionId,
      actualSampleRate: options.actualSampleRate,
      actualChannelCount: options.actualChannelCount,
      error: undefined,
    });

    const unavailableReason = this.configurationError(this.handshakeApiKey);
    if (unavailableReason) {
      this.update({
        ...this.snapshot,
        state: "unavailable",
        error: unavailableReason,
      });
      this.log("streaming_unavailable", { errorCode: "configuration" });
      return;
    }
    this.connect();
  }

  /**
   * Accepts ordered chunks while the proxy handshake is pending. Chunks are
   * removed immediately after WebSocket.send succeeds and are never replayed.
   */
  enqueue(chunk: RawChunk): boolean {
    if (this.disposed || this.stopping || this.isTerminalState()) return false;
    // Keep a bounded buffer only for the initial handshake. After a real
    // proxy session disconnects, accepted frames must never be replayed and
    // fresh frames are deliberately discarded until that session resumes.
    if (this.established && !this.acknowledged) {
      this.log("audio_chunk_discarded", {
        sequenceNumber: chunk.sequenceNumber,
        errorCode: "proxy_reconnecting",
      });
      return false;
    }
    if (chunk.frames.length === 0) return true;
    if (!Number.isInteger(chunk.sequenceNumber) || chunk.sequenceNumber < 0) {
      this.degrade("Received an invalid PCM chunk sequence number.", "invalid_sequence");
      return false;
    }
    if (!Number.isFinite(chunk.durationMs) || chunk.durationMs < 0) {
      this.degrade("Received an invalid PCM chunk duration.", "invalid_chunk");
      return false;
    }
    if (
      this.queue.length + 1 > this.options.maxBufferedChunks ||
      this.queueDurationMs + chunk.durationMs > this.options.maxBufferedDurationMs
    ) {
      this.degrade(
        "Audio streaming buffer is full; local recording continues without streaming.",
        "queue_overflow",
      );
      return false;
    }

    const message = this.options.protocol.createAudioMessage({
      sequenceNumber: chunk.sequenceNumber,
      frames: float32ToBase64(concatenateChunkSamples(chunk)),
    });
    this.queue.push({
      sequenceNumber: chunk.sequenceNumber,
      message,
      durationMs: chunk.durationMs,
    });
    this.queueDurationMs += chunk.durationMs;
    this.update({
      ...this.snapshot,
      nextSequenceNumber: Math.max(
        this.snapshot.nextSequenceNumber,
        chunk.sequenceNumber + 1,
      ),
      queuedChunks: this.queue.length,
      queuedDurationMs: this.queueDurationMs,
    });
    this.log("audio_chunk_buffered", { sequenceNumber: chunk.sequenceNumber });
    this.drain();
    return true;
  }

  flush(): void {
    this.drain();
  }

  /**
   * LongformAudioProxy requires muted audio to continue flowing as measured
   * silence. This flag is UI metadata only; it deliberately does not reject
   * chunks emitted by a still-running worklet.
   */
  pause(): void {
    if (this.isTerminalState() || this.disposed) return;
    this.update({ ...this.snapshot, paused: true });
    this.drain();
  }

  resume(): void {
    if (this.isTerminalState() || this.disposed) return;
    this.update({ ...this.snapshot, paused: false });
    this.drain();
  }

  async stop(): Promise<void> {
    if (this.stopPromise) return this.stopPromise;
    this.stopPromise = this.stopInternal();
    return this.stopPromise;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopping = true;
    this.clearTimers();
    this.resolveEndWaiters(false);
    this.closeConnection(1000, "audio streaming disposed");
    this.clearQueue();
    this.handshakeApiKey = "";
    this.update({ ...this.snapshot, state: "stopped", paused: true });
  }

  private async stopInternal(): Promise<void> {
    if (this.disposed) return;
    this.stopping = true;
    this.clearTimer("reconnectTimer");
    this.update({ ...this.snapshot, state: "ending", paused: true });
    this.drain();

    if (this.acknowledged && this.queue.length > 0) {
      await this.waitForDrainOrTimeout(this.options.stopDrainTimeoutMs);
    }

    let endingError: string | undefined;
    if (this.acknowledged && this.connection) {
      const waitForEnd = this.waitForSessionEndOrTimeout(
        this.options.endTimeoutMs,
      );
      try {
        this.connection.send(this.options.protocol.createEndSessionMessage());
        this.log("streaming_end_requested");
        const ended = await waitForEnd;
        if (!ended) {
          endingError =
            "Audio proxy did not confirm session completion; its server session remains resumable.";
        }
      } catch (error) {
        this.resolveEndWaiters(false);
        endingError = errorMessage(error);
      }
    } else if (this.proxySessionId) {
      endingError =
        "Audio proxy was disconnected before session completion; its server session remains resumable.";
    }

    this.clearTimers();
    this.closeConnection(1000, "audio streaming stopped");
    this.clearQueue();
    this.handshakeApiKey = "";
    this.update({
      ...this.snapshot,
      state: "stopped",
      paused: true,
      error: endingError,
    });
    this.log("streaming_stopped", { errorMessage: endingError });
  }

  private connect(): void {
    if (
      this.disposed ||
      this.stopping ||
      this.isTerminalState() ||
      !this.options.transport
    ) {
      return;
    }

    this.clearTimer("reconnectTimer");
    this.connectionAbort?.abort();
    const abort = new AbortController();
    this.connectionAbort = abort;
    this.acknowledged = false;
    const generation = ++this.connectionGeneration;
    this.update({
      ...this.snapshot,
      state: "connecting",
      bufferedAmount: 0,
      error: undefined,
    });
    this.log("streaming_connecting");

    let connectionReady = false;
    const afterConnectionReady = (callback: () => void) => {
      if (abort.signal.aborted) return;
      if (connectionReady) {
        callback();
        return;
      }
      queueMicrotask(() => {
        if (!abort.signal.aborted) callback();
      });
    };
    const handlers: AudioProxyConnectionHandlers = {
      onOpen: () => afterConnectionReady(() => this.handleOpen(generation)),
      onMessage: (message) =>
        afterConnectionReady(() => this.handleMessage(generation, message)),
      onClose: (event) =>
        afterConnectionReady(() =>
          this.handleConnectionFailure(generation, event),
        ),
      onError: (error) =>
        afterConnectionReady(() =>
          this.failConnection(generation, { code: 0, reason: error.message }),
        ),
    };

    try {
      this.connection = this.options.transport.connect(this.options.url ?? "", handlers);
      connectionReady = true;
      this.connectTimer = this.options.schedule(() => {
        if (this.isCurrentGeneration(generation) && !this.acknowledged) {
          this.failConnection(generation, {
            code: 4000,
            reason: "Audio proxy connection timed out.",
          });
        }
      }, this.options.connectTimeoutMs);
    } catch (error) {
      connectionReady = true;
      this.handleConnectionFailure(generation, {
        code: 0,
        reason: errorMessage(error),
      });
    }
  }

  private handleOpen(generation: number): void {
    if (!this.isCurrentGeneration(generation) || this.stopping) return;
    this.clearTimer("connectTimer");
    this.handshakeStartedAt = this.options.now();
    this.update({ ...this.snapshot, state: "awaiting_ack" });
    this.log("streaming_awaiting_ack");
    try {
      this.connection!.send(
        this.options.protocol.createStartSessionMessage({
          apiKey: this.handshakeApiKey,
          proxySessionId: this.proxySessionId,
          sampleRate: PCM_SAMPLE_RATE,
        }),
      );
    } catch (error) {
      this.failConnection(generation, { code: 0, reason: errorMessage(error) });
      return;
    }
    if (this.acknowledged) return;
    this.handshakeTimer = this.options.schedule(() => {
      if (this.isCurrentGeneration(generation) && !this.acknowledged) {
        this.failConnection(generation, {
          code: 4000,
          reason: "Audio proxy handshake timed out.",
        });
      }
    }, this.options.handshakeTimeoutMs);
  }

  private handleMessage(generation: number, message: string): void {
    if (!this.isCurrentGeneration(generation) || this.disposed) return;
    const parsed = this.options.protocol.parseServerMessage(message);
    if (!parsed) {
      this.reportNonfatalProxyError(
        "malformed_message",
        "Audio proxy sent an unrecognized message.",
      );
      return;
    }

    switch (parsed.type) {
      case "session_started":
        this.handleSessionStarted(parsed);
        return;
      case "ack":
        this.update({
          ...this.snapshot,
          lastAcknowledgedSequenceNumber: parsed.seq,
          proxyTotalBytes: parsed.total_bytes,
          error: undefined,
        });
        this.log("proxy_ack", { sequenceNumber: parsed.seq });
        return;
      case "session_ended":
        this.handleSessionEnded(parsed.session_id, parsed.total_bytes);
        return;
      case "error":
        this.handleProxyError(parsed.code, parsed.message);
        return;
    }
  }

  private handleSessionStarted(message: ProxySessionStartedMessage): void {
    if (this.stopping) return;
    const resumingExistingProxySession = Boolean(this.proxySessionId);
    if (this.proxySessionId && this.proxySessionId !== message.session_id) {
      this.makeUnavailable(
        "Audio proxy resumed a different server session than requested.",
        "protocol_violation",
      );
      return;
    }
    if (resumingExistingProxySession && !message.resumed) {
      this.makeUnavailable(
        "Audio proxy did not confirm that the existing session resumed.",
        "protocol_violation",
      );
      return;
    }
    if (message.sample_rate !== PCM_SAMPLE_RATE) {
      this.makeUnavailable(
        `Audio proxy confirmed ${message.sample_rate} Hz instead of ${PCM_SAMPLE_RATE} Hz.`,
        "sample_rate_mismatch",
      );
      return;
    }
    this.proxySessionId = message.session_id;
    this.established = true;
    this.persistProxySessionId();
    this.acknowledged = true;
    this.clearTimer("handshakeTimer");
    const handshakeLatencyMs = this.handshakeStartedAt
      ? Math.max(0, this.options.now() - this.handshakeStartedAt)
      : undefined;
    this.update({
      ...this.snapshot,
      state: "streaming",
      proxySessionId: message.session_id,
      proxyResumed: message.resumed,
      proxyTotalBytes: message.total_bytes,
      reconnectAttempt: 0,
      handshakeLatencyMs,
      error: undefined,
    });
    this.log("streaming_acknowledged", { handshakeLatencyMs });
    this.drain();
  }

  private handleSessionEnded(proxySessionId: string, totalBytes: number): void {
    if (!this.proxySessionId || this.proxySessionId !== proxySessionId) return;
    this.sessionEnded = true;
    this.acknowledged = false;
    this.clearPersistedProxySessionId();
    this.update({
      ...this.snapshot,
      proxyTotalBytes: totalBytes,
      error: undefined,
    });
    this.resolveEndWaiters(true);
    this.log("streaming_ended", {});

    if (!this.stopping) {
      this.clearTimers();
      this.closeConnection(1000, "proxy session ended");
      this.clearQueue();
      this.update({ ...this.snapshot, state: "stopped", paused: true });
    }
  }

  private handleProxyError(code: LongformProxyErrorCode, message: string): void {
    this.log("proxy_error", { errorCode: code, errorMessage: message });
    if (isPermanentProxyError(code)) {
      if (code === "unknown_session") this.clearPersistedProxySessionId();
      this.makeUnavailable(message, code);
      return;
    }
    this.reportNonfatalProxyError(code, message);
  }

  private reportNonfatalProxyError(
    code: LongformProxyErrorCode | "malformed_message",
    message: string,
  ): void {
    if (this.isTerminalState()) return;
    this.update({ ...this.snapshot, error: message });
    this.log("proxy_nonfatal_error", { errorCode: code, errorMessage: message });
  }

  private makeUnavailable(message: string, errorCode: string): void {
    if (this.isTerminalState()) return;
    this.update({
      ...this.snapshot,
      state: "unavailable",
      bufferedAmount: 0,
      error: message,
    });
    this.clearTimers();
    this.resolveEndWaiters(false);
    this.closeConnection(1008, errorCode);
    this.log("streaming_unavailable", { errorCode, errorMessage: message });
  }

  private failConnection(generation: number, event: AudioProxyCloseEvent): void {
    if (!this.isCurrentGeneration(generation)) return;
    const connection = this.connection;
    try {
      connection?.close(event.code, event.reason);
    } catch {
      // Failure handling below remains valid if close itself throws.
    }
    this.handleConnectionFailure(generation, event);
  }

  private handleConnectionFailure(
    generation: number,
    event: AudioProxyCloseEvent,
  ): void {
    if (
      !this.isCurrentGeneration(generation) ||
      this.handledGenerations.has(generation)
    ) {
      return;
    }
    this.handledGenerations.add(generation);
    this.connection = undefined;
    this.connectionAbort?.abort();
    this.connectionAbort = undefined;
    this.acknowledged = false;
    this.clearTimer("connectTimer");
    this.clearTimer("handshakeTimer");
    this.clearTimer("backpressureTimer");

    if (this.established) this.clearQueue();

    if (this.disposed || this.stopping || this.isTerminalState()) {
      this.resolveEndWaiters(false);
      return;
    }
    if (event.code === 1008) {
      this.makeUnavailable(
        "Audio proxy rejected this connection; local recording continues.",
        "policy_close",
      );
      return;
    }
    if (event.code === 1000) {
      this.makeUnavailable(
        "Audio proxy closed the session before completion was confirmed.",
        "unexpected_close",
      );
      return;
    }
    this.scheduleReconnect(event.reason || "Audio proxy disconnected.");
  }

  private scheduleReconnect(reason: string): void {
    if (this.disposed || this.stopping || this.isTerminalState()) return;
    const attempt = this.snapshot.reconnectAttempt;
    const baseDelay =
      this.options.reconnectDelaysMs[
        Math.min(attempt, this.options.reconnectDelaysMs.length - 1)
      ] ?? 10_000;
    const delayMs = Math.max(0, this.options.jitter(baseDelay, attempt));
    this.update({
      ...this.snapshot,
      state: "reconnecting",
      reconnectAttempt: attempt + 1,
      bufferedAmount: 0,
      error: reason,
    });
    this.log("streaming_reconnecting", {
      reconnectAttempt: attempt + 1,
      errorMessage: reason,
    });
    this.reconnectTimer = this.options.schedule(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delayMs);
  }

  private drain(): void {
    if (
      !this.acknowledged ||
      !this.connection ||
      this.disposed ||
      this.isTerminalState()
    ) {
      return;
    }

    while (this.queue.length > 0) {
      const connection = this.connection;
      if (!connection) return;
      const bufferedAmount = connection.bufferedAmount;
      if (bufferedAmount > this.options.maxSocketBufferedAmount) {
        this.update({ ...this.snapshot, bufferedAmount });
        this.scheduleBackpressurePoll();
        return;
      }

      const next = this.queue[0];
      try {
        connection.send(next.message);
      } catch (error) {
        this.failConnection(this.connectionGeneration, {
          code: 0,
          reason: errorMessage(error),
        });
        return;
      }
      // send() accepted the message. There is no per-frame server receipt ACK,
      // so replaying it on a later socket would duplicate timeline audio.
      this.queue.shift();
      this.queueDurationMs = Math.max(0, this.queueDurationMs - next.durationMs);
      this.update({
        ...this.snapshot,
        queuedChunks: this.queue.length,
        queuedDurationMs: this.queueDurationMs,
        bufferedAmount: connection.bufferedAmount,
      });
      this.log("audio_chunk_sent", { sequenceNumber: next.sequenceNumber });
    }
    this.resolveDrainWaiters();
  }

  private scheduleBackpressurePoll(): void {
    if (this.backpressureTimer !== undefined) return;
    this.backpressureTimer = this.options.schedule(() => {
      this.backpressureTimer = undefined;
      this.drain();
    }, this.options.backpressurePollMs);
  }

  private async waitForDrainOrTimeout(timeoutMs: number): Promise<void> {
    if (this.queue.length === 0) return;
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.drainWaiters.delete(finish);
        this.options.cancel(timeout);
        resolve();
      };
      const timeout = this.options.schedule(finish, timeoutMs);
      this.drainWaiters.add(finish);
      this.drain();
    });
  }

  private async waitForSessionEndOrTimeout(timeoutMs: number): Promise<boolean> {
    if (this.sessionEnded) return true;
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ended: boolean) => {
        if (settled) return;
        settled = true;
        this.endWaiters.delete(finish);
        this.options.cancel(timeout);
        resolve(ended);
      };
      const timeout = this.options.schedule(() => finish(false), timeoutMs);
      this.endWaiters.add(finish);
    });
  }

  private resolveDrainWaiters(): void {
    if (this.queue.length > 0) return;
    for (const resolve of this.drainWaiters) resolve();
    this.drainWaiters.clear();
  }

  private resolveEndWaiters(ended: boolean): void {
    for (const resolve of this.endWaiters) resolve(ended);
    this.endWaiters.clear();
  }

  private degrade(message: string, errorCode: string): void {
    if (this.isTerminalState()) return;
    this.update({
      ...this.snapshot,
      state: "degraded",
      bufferedAmount: 0,
      error: message,
    });
    this.clearTimers();
    this.closeConnection(4008, "audio streaming buffer overflow");
    this.log("streaming_degraded", { errorCode, errorMessage: message });
  }

  private configurationError(apiKey: string): string | undefined {
    if (this.options.configurationError) return this.options.configurationError;
    if (this.options.mode === "disabled") {
      return "Audio streaming is unavailable because it is disabled by configuration.";
    }
    if (!this.options.transport) {
      return "Audio streaming is unavailable because no transport is configured.";
    }
    if (!apiKey) {
      return "Audio streaming is unavailable because the proxy API key is missing.";
    }
    if (this.options.mode === "websocket" && !isWebSocketUrl(this.options.url)) {
      return "Audio streaming is unavailable because the proxy URL is invalid.";
    }
    return undefined;
  }

  private closeConnection(code?: number, reason?: string): void {
    const connection = this.connection;
    this.connection = undefined;
    this.connectionAbort?.abort();
    this.connectionAbort = undefined;
    this.acknowledged = false;
    try {
      connection?.close(code, reason);
    } catch {
      // Stop/dispose must remain idempotent for a broken browser socket.
    }
  }

  private clearQueue(): void {
    this.queue = [];
    this.queueDurationMs = 0;
    this.update({
      ...this.snapshot,
      queuedChunks: 0,
      queuedDurationMs: 0,
    });
    this.resolveDrainWaiters();
  }

  private clearTimers(): void {
    this.clearTimer("connectTimer");
    this.clearTimer("handshakeTimer");
    this.clearTimer("reconnectTimer");
    this.clearTimer("backpressureTimer");
  }

  private clearTimer(
    name: "connectTimer" | "handshakeTimer" | "reconnectTimer" | "backpressureTimer",
  ): void {
    const handle = this[name];
    if (handle !== undefined) this.options.cancel(handle);
    this[name] = undefined;
  }

  private isCurrentGeneration(generation: number): boolean {
    return generation === this.connectionGeneration && !this.disposed;
  }

  private isTerminalState(): boolean {
    return ["unavailable", "degraded", "stopped"].includes(this.snapshot.state);
  }

  private storageKey(): string | undefined {
    return this.localSessionId
      ? `${AUDIO_PROXY_SESSION_STORAGE_PREFIX}${this.localSessionId}`
      : undefined;
  }

  private readPersistedProxySessionId(): string | undefined {
    const key = this.storageKey();
    if (!key) return undefined;
    try {
      return this.options.proxySessionStorage?.getItem(key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private persistProxySessionId(): void {
    const key = this.storageKey();
    if (!key || !this.proxySessionId) return;
    try {
      this.options.proxySessionStorage?.setItem(key, this.proxySessionId);
    } catch {
      // Storage denial does not interrupt live local recording or proxy streaming.
    }
  }

  private clearPersistedProxySessionId(): void {
    const key = this.storageKey();
    if (!key) return;
    try {
      this.options.proxySessionStorage?.removeItem(key);
    } catch {
      // No-op: browser privacy settings may deny sessionStorage access.
    }
  }

  private update(snapshot: AudioStreamingSnapshot): void {
    this.snapshot = snapshot;
    for (const listener of this.listeners) listener(snapshot);
  }

  private log(
    event: string,
    extra: Partial<AudioStreamingLoggerFields> = {},
  ): void {
    this.options.logger?.(event, {
      sessionId: this.localSessionId,
      proxySessionId: this.proxySessionId,
      state: this.snapshot.state,
      queueDepth: this.queue.length,
      queueDurationMs: this.queueDurationMs,
      bufferedAmount: this.connection?.bufferedAmount ?? 0,
      ...extra,
    });
  }
}

export function createAudioStreamingSession(
  options: AudioStreamingSessionOptions = {},
): AudioStreamingSession {
  return new AudioStreamingSession(options);
}

function defaultProxySessionStorage(): ProxySessionStorage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

function jitteredDelay(delayMs: number): number {
  const spread = delayMs * 0.2;
  return Math.round(delayMs - spread + Math.random() * spread * 2);
}

function isWebSocketUrl(value: string | undefined): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "ws:" || url.protocol === "wss:";
  } catch {
    return false;
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Audio proxy connection failed.";
}

function stringEnvironmentValue(value: string | boolean | undefined):
  | string
  | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * A typed-in session key wins over the Vite fallback but is deliberately kept
 * only on the live session instance. Empty input is treated as no override so
 * existing build-time configuration remains backwards compatible.
 */
function resolveSessionApiKey(
  sessionKey: string | undefined,
  apiKey: string | undefined,
  configuredApiKey: string,
): string {
  return (
    stringEnvironmentValue(sessionKey) ??
    stringEnvironmentValue(apiKey) ??
    configuredApiKey
  );
}

function concatenateChunkSamples(chunk: RawChunk): Float32Array {
  const sampleCount = chunk.frames.reduce(
    (total, frame) => total + frame.samples.length,
    0,
  );
  const samples = new Float32Array(sampleCount);
  let offset = 0;
  for (const frame of chunk.frames) {
    samples.set(frame.samples, offset);
    offset += frame.samples.length;
  }
  return samples;
}
